/**
 * What a canary version IS. One owner, because this fact has many readers.
 *
 * A canary build is published on every merge to the default branch and converged
 * through the same rail a customer's update uses. Its version is the next patch
 * above the last stable cut, carrying a SemVer §9 pre-release identifier:
 *
 *     0.7.2-canary.5
 *
 * That shape is read by the update comparator, the release-manifest schema, the
 * manifest generator, the channel tag builders and the publisher. Spelled once
 * here, imported everywhere else — a second spelling is how a schema ends up
 * accepting a version the comparator rejects, and neither side ever notices.
 *
 * WHY THE IDENTIFIER MUST BE A MONOTONIC INTEGER (the one subtle rule):
 * SemVer §11 compares dot-separated pre-release identifiers numerically only when
 * every character is a digit, and ASCII-lexically otherwise. A commit sha would
 * therefore sort builds by alphabet — `canary.f3a` "after" `canary.9c1` — which is
 * not an ordering at all, and the update rail would offer builds in an arbitrary
 * order while looking perfectly healthy. Use a monotonically increasing integer
 * (in CI, the run number).
 *
 * Two orderings follow from this shape and both are load-bearing:
 *   0.7.2-canary.5 > 0.7.1   — the canary is genuinely ahead of the last stable
 *   0.7.2-canary.5 < 0.7.2   — the real cut always supersedes the canary (§11)
 */

/** The pre-release identifier that marks a build as a canary. Spelled once. */
export const CANARY_IDENTIFIER = "canary";

/** `X.Y.Z` — a stable platform version, bare. */
const STABLE_CORE = String.raw`\d+\.\d+\.\d+`;

/** The pre-release suffix, integer-only by construction (see the note above). */
const CANARY_SUFFIX = String.raw`-${CANARY_IDENTIFIER}\.(?:0|[1-9]\d*)`;

/** A canary version, bare: `0.7.2-canary.5`. */
export const CANARY_VERSION_PATTERN = `^${STABLE_CORE}${CANARY_SUFFIX}$`;

/**
 * Any platform version this system accepts, bare — stable or canary. This is the
 * string `release-manifest.schema.json` carries for `platform.version`; the guard
 * test in canary-version.test.ts holds the two equal.
 */
export const PLATFORM_VERSION_PATTERN = `^${STABLE_CORE}(?:${CANARY_SUFFIX})?$`;

/** The same, in release-tag form: `platform-v0.7.2-canary.5`. */
export const PLATFORM_TAG_PATTERN = `^platform-v${STABLE_CORE}(?:${CANARY_SUFFIX})?$`;

const CANARY_VERSION_RE = new RegExp(CANARY_VERSION_PATTERN);
const STABLE_CORE_RE = new RegExp(`^${STABLE_CORE}$`);

/** Strip the tag/v prefix so callers may pass any of the three forms in use. */
function bare(version: string): string {
  return version.trim().replace(/^platform-/, "").replace(/^v/, "");
}

/**
 * Build the canary version for a base version and a run number.
 *
 * Refuses rather than coerces: a bad base or a non-integer identifier is a
 * publishing bug, and a canary version that silently came out malformed would be
 * discovered as an unorderable release channel long after the fact.
 */
export function canaryVersion(baseVersion: string, runNumber: number): string {
  const base = bare(baseVersion);
  if (!STABLE_CORE_RE.test(base)) {
    throw new Error(`canary base version must be X.Y.Z, got '${baseVersion}'`);
  }
  if (typeof runNumber !== "number" || !Number.isInteger(runNumber) || runNumber < 0) {
    throw new Error(
      `canary run number must be a non-negative integer (SemVer orders non-numeric identifiers lexically), got '${String(runNumber)}'`,
    );
  }
  return `${base}-${CANARY_IDENTIFIER}.${String(runNumber)}`;
}

/** Is this a canary version? Accepts bare, v-prefixed and tag form. */
export function isCanaryVersion(version: string): boolean {
  return CANARY_VERSION_RE.test(bare(version));
}
